import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

const HomeNewsletter = () => {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await subscribeNewsletter(email, lang);
      setSuccess(true);
      setEmail("");
    } catch (_err) {
      toast.error(isFr ? "Email invalide" : "Invalid email");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="home-newsletter"
      className="bg-[#F5EFE6] border-y border-[#dcd7ce]"
    >
      <div className="max-w-[1100px] mx-auto px-6 sm:px-10 md:px-16 py-24 md:py-28 text-center">
        <div className="eyebrow mb-4 text-[#8a6d3b]">
          {isFr ? "Offre de bienvenue" : "Welcome offer"}
        </div>
        <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight max-w-2xl mx-auto">
          {isFr
            ? "-10% sur votre 1ère commande."
            : "10% off your first order."}
        </h2>
        <p className="mt-6 text-[16px] md:text-[17px] text-[#4a4a4a] leading-relaxed max-w-md mx-auto">
          {isFr
            ? "Laissez votre email — recevez votre code de bienvenue immédiatement, ainsi que nos rituels confort. Un seul email par mois, jamais plus."
            : "Leave your email — receive your welcome code instantly, along with our comfort rituals. One email a month, never more."}
        </p>

        {success ? (
          <div
            data-testid="home-newsletter-success"
            className="mt-10 max-w-md mx-auto inline-flex items-center justify-center gap-3 bg-white/70 border border-[#dcd7ce] px-6 py-5 rounded-sm"
          >
            <Check size={18} strokeWidth={1.6} className="text-[#3D2B1F]" />
            <div className="text-left">
              <div className="text-sm font-medium text-[#3D2B1F]">
                {isFr ? "Merci ! Vérifiez votre boîte mail" : "Thank you! Check your inbox"}
              </div>
              <div className="text-[13px] text-[#4a4a4a] mt-1">
                {isFr
                  ? "Votre code de bienvenue vous attend."
                  : "Your welcome code is waiting for you."}
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-testid="home-newsletter-form"
            className="mt-10 max-w-md mx-auto"
          >
            <div className="flex items-center bg-white/60 border border-[#3D2B1F]/30 focus-within:border-[#3D2B1F] transition-colors rounded-sm overflow-hidden">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={isFr ? "votre.email@exemple.com" : "your.email@example.com"}
                data-testid="home-newsletter-input"
                className="flex-1 bg-transparent px-5 py-4 outline-none placeholder:text-[#4a4a4a]/70 text-[15px] text-[#3D2B1F]"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="home-newsletter-submit"
                className="bg-[#3D2B1F] text-white px-6 py-4 text-xs tracking-[0.22em] uppercase font-medium hover:bg-[#1A1A1A] disabled:opacity-60 transition-colors inline-flex items-center gap-2"
              >
                {submitting
                  ? (isFr ? "Envoi…" : "Sending…")
                  : (isFr ? "Recevoir -10%" : "Get -10%")}
                <ArrowRight size={14} strokeWidth={1.6} />
              </button>
            </div>
            <p className="text-xs text-[#4a4a4a] mt-4">
              {isFr
                ? "En vous inscrivant, vous acceptez de recevoir nos emails. Désinscription en un clic."
                : "By subscribing you agree to receive our emails. Unsubscribe in one click."}
            </p>
          </form>
        )}
      </div>
    </section>
  );
};

export default HomeNewsletter;
